import { Control, Controller } from 'react-hook-form';
import { CURRENCY_USD } from 'constants/currency';
import { EventTypesEnum } from 'types/generated/client';
import { getEventExperience } from 'utils/shared/events/getEventExperience';
import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import { pluralize } from 'utils/shared/pluralize';
import { isPackGroup } from 'utils/shared/time/getEventSchedule';
import CloseIcon from 'svg/CloseIcon';
import Trash from 'svg/TrashOutline';
import { ButtonText } from 'components/Button';
import InfoAlert from 'components/InfoAlert';
import classNames from 'styles/utils/classNames';
import { GroupsFormData } from '../types';

interface AddEventFormFieldProps {
  field: { id: string };
  index: number;
  control: Control<GroupsFormData>;
  formErrors?: unknown;
  selectedGroupId: string | undefined;
  event: {
    id: string;
    title: string;
    type?: EventTypesEnum | null;
    currency?: string | null;
    groups: Array<{
      id: string;
      title: string | null;
      priceUnitAmount: number;
      timeSlots?: { id: string }[] | null;
    }>;
  };
  groupedIdsUsed: string[];
  existingEventCount: number;
  watchedGroupsLength: number;
  remove: (index: number) => void;
  invitationTeamMembers?: { groupId: string; name: string; id: string }[] | null;
  isIgnoreInvitation?: boolean;
  setIsIgnoreInvitation: (value: boolean) => void;
  /** When true and event has single group, show VAIR round robin UI (title + algorithm message, no dropdown) */
  isVairRoundRobin?: boolean;
  /** When true, hide price display */
  isPrivateWithOutCost?: boolean;
  /** Override event number display (e.g. AddEvents uses index+1, UserEventList uses index+existingEventCount+1) */
  eventDisplayNumber?: number;
  /** Optional className for the outer container */
  containerClassName?: string;
  hasPartner?: boolean;
  onHasPartnerChange?: (value: boolean) => void;
}

export default function AddEventFormField({
  field,
  index,
  control,
  selectedGroupId,
  event,
  groupedIdsUsed,
  existingEventCount,
  watchedGroupsLength,
  remove,
  invitationTeamMembers,
  isIgnoreInvitation,
  setIsIgnoreInvitation,
  isVairRoundRobin = false,
  isPrivateWithOutCost = false,
  eventDisplayNumber,
  containerClassName,
  hasPartner = true,
  onHasPartnerChange,
}: AddEventFormFieldProps) {
  const matchingGroup = event.groups.find((group) => group.id === selectedGroupId);
  const groupIdsToRemove = groupedIdsUsed.filter((id) => id !== selectedGroupId);
  const options = event.groups.filter((group) => !groupIdsToRemove.includes(group.id));
  const teamMembersForGroupInvitation =
    invitationTeamMembers?.filter((tm) => tm.groupId === selectedGroupId) || [];

  const displayNumber = eventDisplayNumber ?? index + existingEventCount + 1;
  const heading =
    matchingGroup && isPackGroup(matchingGroup)
      ? pluralize({ count: matchingGroup.timeSlots?.length ?? 0, singular: 'session' })
      : `Event #${displayNumber}`;
  const isVairSingleGroup = isVairRoundRobin && event.groups.length === 1;
  const { hasPartners } = getEventExperience(event);
  const showPartnerSection = !teamMembersForGroupInvitation?.length || isIgnoreInvitation;

  return (
    <div key={field.id} className={classNames('flex flex-col gap-4', containerClassName ?? 'mt-6')}>
      {!isVairSingleGroup && (
        <div className="flex justify-between">
          <h2 className="typography-product-body-highlight text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            {heading}
          </h2>
          <div className="flex items-center">
            {!!matchingGroup && !isPrivateWithOutCost && (
              <p className="typography-product-body-highlight text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                {
                  convertUnitPriceToFormattedPrice(
                    matchingGroup.priceUnitAmount,
                    event.currency || CURRENCY_USD,
                  ).priceDisplay
                }
              </p>
            )}
            {watchedGroupsLength > 1 && (
              <div className="ml-4">
                <ButtonText onClick={() => remove(index)}>
                  <Trash className="h-5 w-5 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon" />
                </ButtonText>
              </div>
            )}
          </div>
        </div>
      )}
      {isVairSingleGroup ? (
        <div>
          <p className="typography-product-body-highlight text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            {event.groups[0].title}
          </p>
          <div className="mt-3">
            <p className="typography-product-text-card text-color-text-lightmode-tertiary dark:text-color-text-lightmode-tertiary">
              Teams will be assigned automatically during prepare-gameday based on the VAIR
              algorithm.
            </p>
          </div>
          <Controller
            name={`groups.${index}.groupId`}
            control={control}
            render={({ field: controllerField }) => (
              <input type="hidden" {...controllerField} value={event.groups[0].id} />
            )}
          />
        </div>
      ) : (
        <div>
          <div>
            <Controller
              name={`groups.${index}.groupId`}
              control={control}
              render={({ field }) => (
                <select
                  {...field}
                  required
                  className={classNames(
                    'input-form cursor-pointer pr-10', // accounting for arrow
                    matchingGroup
                      ? 'text-color-text-lightmode-primary dark:text-color-text-darkmode-primary'
                      : 'text-color-text-lightmode-placeholder dark:text-color-text-darkmode-placeholder',
                  )}
                >
                  <option value="" disabled>
                    Event
                  </option>
                  {options.map((group) => (
                    <option key={group.id} value={group.id} className="cursor-pointer">
                      {group.title}
                    </option>
                  ))}
                </select>
              )}
            />
          </div>
          {showPartnerSection && hasPartners && (
            <>
              <div className="mt-4 flex flex-col gap-3">
                {[
                  { label: 'I have a partner', value: true },
                  { label: "I don't have a partner", value: false },
                ].map(({ label, value }) => (
                  <label key={label} className="flex cursor-pointer items-center gap-3">
                    <div className="relative h-5 w-5 shrink-0 rounded-full border">
                      <input
                        type="radio"
                        name="partnerStatus"
                        className="peer hidden"
                        checked={hasPartner === value}
                        onChange={() => onHasPartnerChange?.(value)}
                      />
                      <div className="absolute inset-0 rounded-full p-1.5 peer-checked:bg-color-bg-lightmode-invert">
                        <div className="h-full w-full rounded-full bg-color-bg-lightmode-primary" />
                      </div>
                    </div>
                    <span className="typography-product-element-label text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                      {label}
                    </span>
                  </label>
                ))}
              </div>
              {!hasPartner && (
                <div className="mt-3">
                  <InfoAlert
                    title="Register without a partner"
                    desc={`Once you register, you will be able to contact other players who do not have a partner. The ${
                      event.type === EventTypesEnum.Social ? 'event' : 'tournament'
                    } is not responsible for partner matching.`}
                  />
                </div>
              )}
            </>
          )}
          {!showPartnerSection && (
            <div className="mt-3">
              <div>
                <div>
                  <span className="typography-product-element-label">Team members:</span>{' '}
                  <span className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    {teamMembersForGroupInvitation.map(({ name }) => name).join(', ')}
                  </span>
                </div>
              </div>
              {/* <div className="typography-product-text-card mt-0.5">
                <ButtonText
                  className="flex items-center"
                  onClick={() => setIsIgnoreInvitation(true)}
                >
                  <CloseIcon className="mr-0.5 h-3.5 w-3.5 text-color-brand-primary" />{' '}
                  <span className="text-color-text-lightmode-secondary dark:text-color-text-lightmode-secondary">
                    Choose different team
                  </span>
                </ButtonText>
              </div> */}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
